
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';
import ErrorToast from '../components/ErrorToast';
import Paginacao from '../components/Paginacao';
import { FaUser, FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaCheckCircle, FaTimesCircle, FaExclamationCircle, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';
import './ClienteDetail.css';

const InfoCard = ({ title, icon, children }) => (
  <div className="info-card">
    <div className="info-card-header">
      {icon}
      <h2>{title}</h2>
    </div>
    <div className="info-card-body">
      {children}
    </div>
  </div>
);

const InfoRow = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="info-row">
      <span className="info-label">{label}</span>
      <span className="info-value">{String(value)}</span>
    </div>
  );
};

const EnderecoCard = ({ title, endereco }) => {
  if (!endereco || !Object.values(endereco).some(v => v)) return null;

  return (
    <div className="endereco-card">
      <div className="endereco-header">
        <FaMapMarkerAlt />
        <h3>{title}</h3>
      </div>
      <div className="endereco-body">
        <InfoRow label="Logradouro" value={endereco.logradouro} />
        <InfoRow label="Número" value={endereco.numero} />
        <InfoRow label="Complemento" value={endereco.complemento} />
        <InfoRow label="Bairro" value={endereco.bairro} />
        <InfoRow label="Cidade" value={endereco.cidade} />
        <InfoRow label="UF" value={endereco.uf} />
        <InfoRow label="CEP" value={endereco.cep} />
      </div>
    </div>
  );
};

const ContatoCard = ({ contato }) => {
  const getIcon = () => {
    if (contato.tipo_contato.toLowerCase().includes('email')) return <FaEnvelope />;
    return <FaPhone />;
  };

  return (
    <div className="contato-card">
      <div className="contato-card-header">
        <div className="contato-icon">{getIcon()}</div>
        <div className="contato-info">
          <span className="contato-valor">{contato.valor}</span>
          <span className="contato-etiqueta">{contato.etiqueta}</span>
        </div>
      </div>
      <div className="contato-tags">
        {contato.is_whatsapp && <span className="tag whatsapp"><FaWhatsapp /> WhatsApp</span>}
        {contato.contato_confirmado && <span className="tag confirmado"><FaCheckCircle /> Confirmado</span>}
        {contato.nao_perturbe && <span className="tag nao-perturbe"><FaTimesCircle /> Não Perturbe</span>}
        {contato.engano && <span className="tag engano"><FaExclamationCircle /> Engano</span>}
      </div>
      {contato.data_ultimo_contato && (
        <p className="contato-data">
          <FaCalendarAlt /> Último contato: {new Date(contato.data_ultimo_contato).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

const EventosCliente = ({ clienteId }) => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [total, setTotal] = useState(0);

  const fetchEventos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/eventos-cliente/cliente/${clienteId}`, {
        params: { page, pageSize }
      });
      setEventos(response.data.data);
      setTotal(response.data.total);
    } catch (err) {
      setError('Erro ao buscar eventos do cliente.');
    } finally {
      setLoading(false);
    }
  }, [clienteId, page, pageSize]);

  useEffect(() => {
    fetchEventos();
  }, [fetchEventos]);

  return (
    <InfoCard title="Histórico de Eventos" icon={<FaCalendarAlt />}>
      {loading && <Loading />}
      {error && <ErrorToast message={error} onClose={() => setError('')} />}
      <div className="table-container">
        <table className="eventos-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Detalhe</th>
              <th>Data</th>
              <th>Usuário</th>
            </tr>
          </thead>
          <tbody>
            {eventos.map(evento => (
              <tr key={evento.id}>
                <td>{evento.tipo_evento}</td>
                <td>{evento.detalhe_evento}</td>
                <td>{new Date(evento.data_evento).toLocaleString()}</td>
                <td>{evento.usuario_cadastro}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginacao
        totalItens={total}
        itensPorPagina={pageSize}
        paginaAtual={page}
        setPaginaAtual={setPage}
      />
    </InfoCard>
  );
};


const ClienteDetail = ({ cliente }) => {
  const { id } = useParams();
  const [data, setData] = useState(cliente || null);
  const [loading, setLoading] = useState(!cliente);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (cliente) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/clientes/${id}/completo`);
        setData(res.data);
      } catch {
        setErro('Erro ao carregar dados do cliente.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, cliente]);

  if (loading) return <Loading />;
  if (erro) return <ErrorToast message={erro} onClose={() => setErro('')} />;
  if (!data) return null;

  const { pessoa_fisica: pf, pessoa_juridica: pj } = data;

  return (
    <div className="cliente-detail-container">
      <header className="cliente-header">
        <div className="cliente-header-icon">
          {data.tipo === 'PF' ? <FaUser size={40} /> : <FaBuilding size={40} />}
        </div>
        <div className="cliente-header-info">
          <h1>{data.nome}</h1>
          <p>{data.documento}</p>
        </div>
        <div className="cliente-header-meta">
          <span>ID: {data.id}</span>
          <span>Origem: {data.origem_cadastro || '-'}</span>
        </div>
      </header>

      <main className="cliente-main-content">
        <div className="cliente-col-1">
          <InfoCard title="Dados Gerais" icon={<FaInfoCircle />}>
            <InfoRow label="Tipo" value={data.tipo} />
            <InfoRow label="Processamento ID" value={data.processamento_id} />
            <InfoRow label="Criado em" value={new Date(data.created_at).toLocaleString()} />
            <InfoRow label="Atualizado em" value={new Date(data.updated_at).toLocaleString()} />
          </InfoCard>

          {data.tipo === 'PF' && pf && (
            <InfoCard title="Dados Pessoais" icon={<FaUser />}>
              <InfoRow label="CPF" value={pf.cpf} />
              <InfoRow label="Sexo" value={pf.sexo} />
              <InfoRow label="Data Nascimento" value={pf.data_nascimento} />
              <InfoRow label="Idade" value={pf.idade} />
              <InfoRow label="Escolaridade" value={pf.escolaridade} />
              <InfoRow label="Renda Estimada" value={pf.renda_estimada} />
              <InfoRow label="Faixa Renda" value={pf.faixa_renda} />
              <InfoRow label="Nome da Mãe" value={pf.nome_mae} />
              <InfoRow label="Status" value={pf.status} />
              <InfoRow label="Prob. Óbito" value={pf.probabilidade_obito} />
            </InfoCard>
          )}

          {data.tipo === 'PJ' && pj && (
            <InfoCard title="Dados da Empresa" icon={<FaBuilding />}>
              <InfoRow label="CNPJ" value={pj.cnpj} />
              <InfoRow label="Razão Social" value={pj.razao_social} />
              <InfoRow label="Fantasia" value={pj.fantasia} />
              <InfoRow label="Data Abertura" value={pj.data_abertura} />
              <InfoRow label="CNAE" value={pj.cnae_descricao} />
              <InfoRow label="Setor" value={pj.cnae_setor} />
              <InfoRow label="Qtd Funcionários" value={pj.qtd_funcionarios} />
              <InfoRow label="Status" value={pj.status} />
            </InfoCard>
          )}

          {data.contatos && data.contatos.length > 0 && (
            <InfoCard title="Contatos" icon={<FaPhone />}>
              <div className="contatos-grid">
                {data.contatos.map(c => <ContatoCard key={c.id} contato={c} />)}
              </div>
            </InfoCard>
          )}
        </div>

        <div className="cliente-col-2">
          {data.tipo === 'PF' && pf && (
            <>
              <EnderecoCard title="Endereço Principal" endereco={{ logradouro: pf.logradouro1, numero: pf.numero1, complemento: pf.complemento1, bairro: pf.bairro1, cidade: pf.cidade1, uf: pf.uf1, cep: pf.cep1 }} />
              <EnderecoCard title="Endereço Secundário" endereco={{ logradouro: pf.logradouro2, numero: pf.numero2, complemento: pf.complemento2, bairro: pf.bairro2, cidade: pf.cidade2, uf: pf.uf2, cep: pf.cep2 }} />
            </>
          )}
          {data.tipo === 'PJ' && pj && (
            <>
              <EnderecoCard title="Endereço Principal" endereco={{ logradouro: pj.logradouro, numero: pj.numero, complemento: pj.complemento, bairro: pj.bairro, cidade: pj.cidade, uf: pj.uf, cep: pj.cep }} />
              <EnderecoCard title="Endereço Secundário" endereco={{ logradouro: pj.logradouro2, numero: pj.numero2, complemento: pj.complemento2, bairro: pj.bairro2, cidade: pj.cidade2, uf: pj.uf2, cep: pj.cep2 }} />
            </>
          )}
          
          <EventosCliente clienteId={id} />
        </div>
      </main>
    </div>
  );
};

export default ClienteDetail;
